import {init_choices, on} from './util';
import { initPickers } from './picker';
import * as bootstrap from "bootstrap";
import DataTable from "datatables.net-bs5";
import Coloris from '@melloware/coloris';
import { reThemeAllCharts, updateAccentTaggedCharts } from './utils/chartTheme';
import { localizeInstants } from './utils/instants';
import { initGlossary } from './glossary';

declare global {
    interface Window { choices: any[]; }
}

export default class {
    static init = () => {
        themeToggle();
        colorisInit();
        initPickers(); // unified picker module (D-15, INPUT-01)
        init_choices();
        tooltips();
        initGlossary();
        datatables();
        localizeInstants();
        // DataTables redraw re-localization is wired via DataTable.defaults.drawCallback in datatables() (build-agnostic; native dt.draw does not reliably fire). Page-load localizeInstants() + 60s ticker cover the rest.
        // 60s ticker — Guard A: window singleton prevents double-interval on shared init (D-02)
        if (!(window as any).__ksatInstantsTicker) {
            (window as any).__ksatInstantsTicker = setInterval(localizeInstants, 60_000);
        }
    }
}

// ── Theme toggle (sun/moon, no reload) ───────────────────────────────────────
// Guard pattern B: element property flag prevents double-bind when shared.init()
// runs on every page load (feedback_shared_component_every_page_init_double_bind).

function updateToggleIcon(toggle: HTMLButtonElement, theme: string): void {
    if (theme === 'dark') {
        // Moon SVG — 20×20, fill="currentColor"
        toggle.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';
    } else {
        // Sun SVG — 20×20, fill="currentColor"
        toggle.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="12" r="5" fill="currentColor"/></svg>';
    }
}

function themeToggle(): void {
    // Use querySelectorAll to handle cases where both desktop + mobile toggles exist in DOM
    const toggles = document.querySelectorAll<HTMLButtonElement>('[data-theme-toggle]');
    if (!toggles.length) return;  // Guard C: element absence

    toggles.forEach((toggle) => {
        if ((toggle as any)._ksatThemeBound) return;  // Guard B: element property flag
        (toggle as any)._ksatThemeBound = true;

        toggle.addEventListener('click', () => {
            const html = document.documentElement;
            const current = html.getAttribute('data-bs-theme') ?? 'dark';
            const next = current === 'dark' ? 'light' : 'dark';
            html.setAttribute('data-bs-theme', next);
            reThemeAllCharts(); // Phase 181: re-theme all active Highcharts on toggle (D-03)
            document.cookie = `theme=${next}; max-age=${30 * 24 * 3600}; path=/; SameSite=Lax; Secure`;
            // Update all toggle icons in DOM (desktop + mobile)
            document.querySelectorAll<HTMLButtonElement>('[data-theme-toggle]').forEach(t => {
                updateToggleIcon(t, next);
                // aria-label names the ACTION: "Switch to dark mode" when we just switched to light
                t.setAttribute('aria-label', next === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
            });
        });
    });
}

// ── WCAG contrast helpers ─────────────────────────────────────────────────────

function relativeLuminance(hex: string): number {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16) / 255;
    const g = parseInt(clean.substring(2, 4), 16) / 255;
    const b = parseInt(clean.substring(4, 6), 16) / 255;
    const linearize = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

function contrastRatio(hex1: string, hex2: string): number {
    const l1 = relativeLuminance(hex1);
    const l2 = relativeLuminance(hex2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

const DARK_SURFACE = '#1f1f1f';
const LIGHT_SURFACE = '#ffffff';
const BUTTON_TEXT = '#000000';

function triggerContrastCheck(accentHex: string): void {
    // Use class selector to update ALL readout elements (desktop + mobile both in DOM)
    const els = document.querySelectorAll('.ksat-contrast-readout');
    if (!els.length) return;  // Guard C: element absence (readout only on pages with picker)

    const onDark = contrastRatio(accentHex, DARK_SURFACE);
    const onLight = contrastRatio(accentHex, LIGHT_SURFACE);
    const onAccent = contrastRatio(BUTTON_TEXT, accentHex);

    const badge = (ratio: number): { text: string; cls: string; note: string } => {
        if (ratio >= 4.5) return { text: '✓ AA', cls: 'contrast-pass', note: 'passes AA' };
        if (ratio >= 3.0) return { text: '⚠ AA·lg', cls: 'contrast-warn-lg', note: 'AA for large text only' };
        return { text: '⚠ fail', cls: 'contrast-warn', note: 'fails AA' };
    };

    const line = (label: string, full: string, ratio: number): string => {
        const b = badge(ratio);
        return `<span class="contrast-pair ${b.cls}" title="${full}: ${ratio.toFixed(2)}:1 — ${b.note}">${label} ${ratio.toFixed(1)} ${b.text}</span>`;
    };

    // join('') — pairs are styled as inline pill chips by .ksat-contrast-readout (no <br> stacking)
    const html = [
        line('Dark', 'Accent on dark surface', onDark),
        line('Light', 'Accent on light surface', onLight),
        line('Btn', 'Button label on accent fill', onAccent),
    ].join('');

    els.forEach(el => { el.innerHTML = html; });
}

// ── Coloris accent picker ────────────────────────────────────────────────────
// Guard pattern A: window flag prevents re-init on every page (global Coloris state).
// Guard pattern C: element-absence early-return for pages without the picker.

function colorisInit(): void {
    if ((window as any).__ksatColorisInited) return;  // Guard A: window flag
    const inputs = document.querySelectorAll<HTMLInputElement>('[data-coloris-accent]');
    if (!inputs.length) return;  // Guard C: element absence
    (window as any).__ksatColorisInited = true;

    Coloris.init();
    Coloris({
        el: '[data-coloris-accent]',
        swatches: [
            '#D89702', // Amber (default)
            '#F59E0B', // Yellow
            '#06B6D4', // Cyan
            '#14B8A6', // Teal
            '#10B981', // Green
            '#F97316', // Orange
            '#EC4899', // Pink
            '#3B82F6', // Blue
        ],
        themeMode: 'auto',
        alpha: false,
        formatToggle: false,
    });

    // Live contrast readout on every drag (coloris:pick fires continuously during drag)
    document.addEventListener('coloris:pick', (e: Event) => {
        const pickedHex = (e as CustomEvent).detail.color as string;
        triggerContrastCheck(pickedHex);
        // Phase 181 D-02: live accent recolor of accent-tagged charts during picker drag.
        // reThemeAllCharts() re-reads chrome tokens (accent not in chrome fragment — harmless).
        // updateAccentTaggedCharts() live-recolors trakt/lastfm series + heatmap colorAxis.
        reThemeAllCharts();
        updateAccentTaggedCharts(pickedHex);
    });

    // Save on picker close (change event fires once when picker closes with a selection)
    // Bind to ALL [data-coloris-accent] inputs (desktop + mobile both in DOM)
    const saveAccent = (input: HTMLInputElement) => {
        const hex = input.value;
        const saveUrl = input.getAttribute('data-save-url');
        if (!saveUrl || !hex) return;

        fetch(saveUrl, {
            method: 'POST',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: 'color=' + encodeURIComponent(hex),
        })
        .then(r => r.json())
        .then((data: { ok: boolean }) => {
            if (data.ok) {
                // Update live CSS variables — --ksat-accent + --bs-primary-rgb (Pitfall 5)
                document.documentElement.style.setProperty('--ksat-accent', hex);
                // Phase 181 D-02: after accent is persisted + written to <html style>, re-theme
                // all charts (chrome re-read) and targeted accent series/colorAxis (D-02).
                reThemeAllCharts();
                updateAccentTaggedCharts(hex);
                // Parse hex → r, g, b for --bs-primary-rgb (supports rgba(var(--bs-primary-rgb),...) usages)
                const clean = hex.replace('#', '');
                const r = parseInt(clean.substring(0, 2), 16);
                const g = parseInt(clean.substring(2, 4), 16);
                const b = parseInt(clean.substring(4, 6), 16);
                document.documentElement.style.setProperty('--bs-primary-rgb', `${r}, ${g}, ${b}`);
                // Sync all other accent inputs to the saved value (keep desktop + mobile in sync)
                document.querySelectorAll<HTMLInputElement>('[data-coloris-accent]').forEach(inp => {
                    if (inp !== input) inp.value = hex;
                });
            } else {
                // Warn-only — never block; show error toast via awesome-notifications if available
                const AWN = (window as any).AWN;
                if (AWN) {
                    new AWN().alert('Could not save accent color. Try again.');
                }
            }
        })
        .catch(() => {
            const AWN = (window as any).AWN;
            if (AWN) {
                new AWN().alert('Could not save accent color. Try again.');
            }
        });
    };

    inputs.forEach(input => {
        input.addEventListener('change', () => saveAccent(input));
    });

    // Populate readout immediately with current accent value (before first drag)
    const initialValue = inputs[0].value;
    if (initialValue && /^#[0-9a-fA-F]{6}$/.test(initialValue)) {
        triggerContrastCheck(initialValue);
        // Sync --bs-primary-rgb so rgba(var(--bs-primary-rgb),...) usages (SortableJS drag-flash,
        // transparency variants) track the saved accent on page load — not just after picker
        // interaction (WR-01 fix: tokens.scss seeds amber as compile-time fallback but the server
        // emits --ksat-accent:saved_hex inline on <html>, leaving --bs-primary-rgb stale until
        // the user moves the picker).
        const clean = initialValue.replace('#', '');
        const r = parseInt(clean.substring(0, 2), 16);
        const g = parseInt(clean.substring(2, 4), 16);
        const b = parseInt(clean.substring(4, 6), 16);
        document.documentElement.style.setProperty('--bs-primary-rgb', `${r}, ${g}, ${b}`);
    }
}

function tooltips() {
    [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]')).map(function (tooltipTriggerEl) {
        const el = tooltipTriggerEl as HTMLElement;
        const isHtml = el.dataset.bsHtml === 'true';
        return new bootstrap.Tooltip(el, { html: isHtml, sanitize: !isHtml });
    });
}

function datatables() {
    DataTable.defaults.columnDefs = [
        { targets: '_all', orderSequence: ['desc','asc'] },
    ];
    DataTable.defaults.drawCallback = function () { localizeInstants(); };

    on('click', '.td-editable', (td : HTMLTableCellElement) => {
        let child = td.children[0] as HTMLElement;
        if(child) {
            child.dispatchEvent(new Event('click', { bubbles: true }));
        }
    }, true);
}

